import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Button from "components/Button";
import Stripe from "components/Stripe";
import { AUTH_LOGOUT } from "actions/auth";
import { INITIAL_SYNC_PRODUCTS } from "actions/sync-products";
import { INITIATE_STORES } from "actions/stores";
import {
  getAllCards,
  removeCard,
  checkJWTtoken,
  dtfStripeCharge,
} from "actions/stripe";
import { clearCart } from "actions/dtfCart";
import { ROUTES } from "shared/constants/routes";
import COLORS from "shared/constants/colors";
import {
  getStripePaymentErrorMessage,
  isStripePaymentFailed,
  isStripePaymentSucceeded,
} from "shared/helpers/stripe";
import { formatMoney } from "pages/dtf-transfers/checkout/currency";
import {
  FormSection,
  SectionTitle,
  PaymentCard,
  PaymentText,
  StripeBadge,
} from "styles/pages/dtf-transfers/checkout";

const CART_ORDER_STUB = {
  MarketplaceType: "Shirtly",
  Marketplace: "Shirtly",
};

const PaymentStep = ({ total, checkoutData, currency = "CAD" }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const emailid = useSelector((state) => state?.auth?.profile?.emailid);
  const token = useSelector((state) => state?.auth?.profile?.token);
  const yotiVerificationStatus = useSelector(
    (state) => state?.auth?.profile?.YotiVerificationStatus,
  );

  const [paymentModal, setPaymentModal] = useState(false);
  const [cards, setCards] = useState(undefined);
  const [paymentError, setPaymentError] = useState("");

  const handlePayClick = async () => {
    if (!emailid || total <= 0) return;

    setPaymentError("");

    const isTokenValid = await dispatch(checkJWTtoken(token));

    if (isTokenValid?.tokenExpired !== true) {
      dispatch({ type: INITIATE_STORES });
      dispatch({ type: AUTH_LOGOUT });
      dispatch({ type: INITIAL_SYNC_PRODUCTS });
      return;
    }

    const savedCards = await dispatch(getAllCards({ user: emailid }));
    setCards(savedCards);
    setPaymentModal(true);
  };

  const handlePaymentClose = async (data) => {
    if (!data) {
      setPaymentModal(false);
      return;
    }

    const payload = {
      user: emailid,
      saveCard: data?.saveCard,
      cardDetails: data?.cardDetails,
      currency,
      yotiVerificationStatus,
      amount: parseInt(total * 100),
      checkout: checkoutData,
    };

    const charge = await dispatch(dtfStripeCharge(payload, token));

    if (charge?.tokenExpired) {
      return;
    }

    if (isStripePaymentFailed(charge) || !isStripePaymentSucceeded(charge)) {
      setPaymentModal(false);
      setPaymentError(getStripePaymentErrorMessage(charge));
      return;
    }

    await dispatch(clearCart(emailid, token));
    setPaymentModal(false);
    window.alert("Payment successful. Your order has been placed.");
    navigate(ROUTES.DTF_ORDERS.LINK);
  };

  const currencyLabel = currency === "USD" ? "US dollars (USD)" : "Canadian dollars (CAD)";

  return (
    <>
      <FormSection>
        <SectionTitle>Payment</SectionTitle>

        <PaymentCard>
          <PaymentText>
            All transactions are secure and encrypted. Pay with your credit or
            debit card through Stripe in {currencyLabel}.
          </PaymentText>
          <StripeBadge>Powered by Stripe</StripeBadge>
        </PaymentCard>

        {paymentError ? (
          <PaymentText style={{ color: COLORS.RED, marginTop: "12px" }}>
            {paymentError}
          </PaymentText>
        ) : null}

        <div style={{ marginTop: "20px" }}>
          <Button
            title={`Pay ${formatMoney(total, currency)}`}
            endIcon={<ArrowForwardIcon />}
            onClick={handlePayClick}
            containerStyle={{
              backgroundColor: COLORS.BLUE,
              color: COLORS.WHITE,
              textTransform: "none",
              borderRadius: "6px",
              padding: "12px 20px",
              fontSize: "16px",
              width: "100%",
            }}
          />
        </div>
      </FormSection>

      {paymentModal ? (
        <Stripe
          order={CART_ORDER_STUB}
          marketplace="Shirtly"
          payableAmount={total}
          fixedCurrency={currency}
          open={paymentModal}
          cards={cards}
          hide={handlePaymentClose}
          removeCards={async (card) => {
            const request = await dispatch(
              removeCard({
                user: emailid,
                cardNumber: card?.cardNumber,
              }),
            );

            if (request?.error) return;

            setCards(undefined);
          }}
        />
      ) : null}
    </>
  );
};

export default PaymentStep;
